import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronRight, Loader2, Save, Download } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { HubSaveButton } from "@/components/ui/HubSaveButton";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input, Label, TextArea } from "@/components/ui/Input";
import { CardTitleWithHelp, HoverHintWithGlyph, PageTitleWithHelp } from "@/components/ui/title-help";
import { useHubToast } from "@/components/ui/HubToast";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";
import { downloadPdfBase64 } from "@/lib/miscPrintingApi";
import { ApiError } from "@/lib/api";
import { EM_DASH, cn } from "@/lib/utils";

import {
  CORRECTIVE_ACTIONS_LIST_KEY,
  CORRECTIVE_ACTION_SOURCES,
  addCorrectiveActionComment,
  correctiveActionLabel,
  correctiveActionSourceLabel,
  correctiveActionStatusLabel,
  fetchCorrectiveActionDetail,
  fetchCorrectiveActionPdf,
  updateCorrectiveAction,
  type CorrectiveActionActivity,
  type CorrectiveActionReport,
  type CorrectiveActionSource,
  type CorrectiveActionStatus,
  type UpdateCorrectiveActionInput,
} from "./correctiveActionsApi";
import {
  correctiveActionActivityChangeText,
  summarizeCorrectiveActionActivity,
} from "@integrator-src/api/quality/quality-corrective-action-activity-display";
import { fetchQualityCaseDetail, fetchQualityCases, qualityCaseLabel, type QualityCase } from "./casesApi";
import { QualityOwnerSelect } from "./QualityOwnerSelect";

const DETAIL_HELP = [
  "Work through the ISO 9001 corrective action loop: problem description, immediate containment, root cause analysis,",
  "corrective action plan and implementation, then effectiveness verification before closure.",
  "",
  "Status tracks workflow stage. Every save and status change is recorded in the activity log for audit evidence.",
].join(" ");

function formatDateTime(value: string | null): string {
  if (!value) return EM_DASH;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

function activitySummary(activity: CorrectiveActionActivity): string {
  return summarizeCorrectiveActionActivity(activity);
}

const CORRECTIVE_ACTION_FLOW_STATUSES = [
  "draft",
  "open",
  "containment",
  "analysis",
  "action",
  "verification",
  "closed",
] as const satisfies readonly CorrectiveActionStatus[];

function nextCorrectiveActionStatus(status: CorrectiveActionStatus): CorrectiveActionStatus | null {
  if (status === "cancelled") return null;
  const index = CORRECTIVE_ACTION_FLOW_STATUSES.indexOf(status);
  if (index < 0 || index >= CORRECTIVE_ACTION_FLOW_STATUSES.length - 1) return null;
  return CORRECTIVE_ACTION_FLOW_STATUSES[index + 1] ?? null;
}

function nextStageButtonLabel(status: CorrectiveActionStatus): string {
  if (status === "closed") return "Close";
  return `Move to ${correctiveActionStatusLabel(status)}`;
}

const CONTAINMENT_HELP =
  "Immediate containment is only the short-term control of this incident, such as a return or a temporary hold. A permanent process or code change does not belong here, even when it was implemented immediately. Record that change under corrective action.";

const STATUS_ORDER: CorrectiveActionStatus[] = [...CORRECTIVE_ACTION_FLOW_STATUSES];

function statusReached(status: CorrectiveActionStatus, target: CorrectiveActionStatus): boolean {
  if (status === "cancelled") return true;
  return STATUS_ORDER.indexOf(status) >= STATUS_ORDER.indexOf(target);
}

interface FormState {
  title: string;
  status: CorrectiveActionStatus;
  sourceType: CorrectiveActionSource;
  targetCloseDate: string;
  ownerUserId: string | null;
  problemDescription: string;
  immediateContainment: string;
  rootCauseAnalysis: string;
  correctiveActionPlan: string;
  implementationNotes: string;
  effectivenessVerification: string;
  continuedReviewOn: string;
  qualityCaseId: number | null;
}

function formFromReport(report: CorrectiveActionReport): FormState {
  return {
    title: report.title,
    status: report.status,
    sourceType: report.sourceType,
    targetCloseDate: report.targetCloseDate ?? "",
    ownerUserId: report.ownerUserId,
    problemDescription: report.problemDescription,
    immediateContainment: report.immediateContainment ?? "",
    rootCauseAnalysis: report.rootCauseAnalysis ?? "",
    correctiveActionPlan: report.correctiveActionPlan ?? "",
    implementationNotes: report.implementationNotes ?? "",
    effectivenessVerification: report.effectivenessVerification ?? "",
    continuedReviewOn: report.continuedReviewOn ?? "",
    qualityCaseId: report.qualityCaseId,
  };
}

function buildPatch(form: FormState, baseline: FormState): UpdateCorrectiveActionInput {
  const patch: UpdateCorrectiveActionInput = {};
  if (form.title !== baseline.title) patch.title = form.title;
  if (form.status !== baseline.status) patch.status = form.status;
  if (form.sourceType !== baseline.sourceType) patch.sourceType = form.sourceType;
  if (form.targetCloseDate !== baseline.targetCloseDate) {
    patch.targetCloseDate = form.targetCloseDate.trim() === "" ? null : form.targetCloseDate;
  }
  if (form.ownerUserId !== baseline.ownerUserId) {
    patch.ownerUserId = form.ownerUserId;
  }
  if (form.problemDescription !== baseline.problemDescription) patch.problemDescription = form.problemDescription;
  if (form.immediateContainment !== baseline.immediateContainment) {
    patch.immediateContainment = form.immediateContainment.trim() === "" ? null : form.immediateContainment;
  }
  if (form.rootCauseAnalysis !== baseline.rootCauseAnalysis) {
    patch.rootCauseAnalysis = form.rootCauseAnalysis.trim() === "" ? null : form.rootCauseAnalysis;
  }
  if (form.correctiveActionPlan !== baseline.correctiveActionPlan) {
    patch.correctiveActionPlan = form.correctiveActionPlan.trim() === "" ? null : form.correctiveActionPlan;
  }
  if (form.implementationNotes !== baseline.implementationNotes) {
    patch.implementationNotes = form.implementationNotes.trim() === "" ? null : form.implementationNotes;
  }
  if (form.effectivenessVerification !== baseline.effectivenessVerification) {
    patch.effectivenessVerification =
      form.effectivenessVerification.trim() === "" ? null : form.effectivenessVerification;
  }
  if (form.continuedReviewOn !== baseline.continuedReviewOn) {
    patch.continuedReviewOn = form.continuedReviewOn.trim() === "" ? null : form.continuedReviewOn;
  }
  if (form.qualityCaseId !== baseline.qualityCaseId) {
    patch.qualityCaseId = form.qualityCaseId;
  }
  return patch;
}

function QualityCaseLinkField({
  caseId,
  caseTitle,
  disabled,
  onChange,
}: {
  caseId: number | null;
  caseTitle: string | null;
  disabled: boolean;
  onChange: (caseId: number | null) => void;
}): JSX.Element {
  const [search, setSearch] = useState("");
  const trimmed = search.trim();
  const numericId = /^\d+$/.test(trimmed) ? Number(trimmed) : null;
  const exactQuery = useQuery({
    queryKey: ["quality", "case", "car-link", numericId],
    queryFn: async () => {
      try {
        return await fetchQualityCaseDetail(numericId ?? 0);
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) return null;
        throw error;
      }
    },
    enabled: !disabled && numericId != null && numericId > 0,
  });
  const casesQuery = useQuery({
    queryKey: ["quality", "cases", "car-link", trimmed],
    queryFn: () => fetchQualityCases({ search: trimmed, limit: 8 }),
    enabled: !disabled && numericId == null && trimmed.length >= 2,
  });
  const exactCase = exactQuery.data?.case ?? null;
  const rows = numericId != null ? [] : (casesQuery.data?.rows ?? []);
  const searching = exactQuery.isFetching || casesQuery.isFetching;

  return (
    <div>
      <Label htmlFor="detail-quality-case">Quality case</Label>
      {caseId != null ? (
        <p className="mt-1.5 text-sm">
          <Link className="text-sky-700 hover:underline" to={`/quality/cases/${caseId}`}>
            {qualityCaseLabel(caseId)}
            {caseTitle ? ` · ${caseTitle}` : ""}
          </Link>
        </p>
      ) : (
        <p className="mt-1.5 text-sm text-slate-600">No quality case linked.</p>
      )}
      {disabled ? null : (
        <div className="relative mt-2">
          <Input
            id="detail-quality-case"
            value={search}
            placeholder="Search quality cases"
            onChange={(event) => setSearch(event.target.value)}
          />
          {searching ? <p className="mt-1 text-xs text-slate-600">Searching…</p> : null}
          {exactCase != null ? (
            <button
              type="button"
              className="mt-1 block w-full rounded-md border border-slate-200 px-3 py-2 text-left text-sm hover:bg-slate-50"
              onClick={() => {
                onChange(exactCase.id);
                setSearch("");
              }}
            >
              {qualityCaseLabel(exactCase.id)} · {exactCase.title}
            </button>
          ) : null}
          {numericId != null && exactQuery.isSuccess && exactCase == null ? (
            <p className="mt-1 text-xs text-slate-600">No quality case {numericId}.</p>
          ) : null}
          {rows.length > 0 ? (
            <ul className="mt-1 max-h-48 overflow-auto rounded-md border border-slate-200 bg-white">
              {rows.map((qualityCase: QualityCase) => (
                <li key={qualityCase.id}>
                  <button
                    type="button"
                    className="block w-full px-3 py-2 text-left text-sm hover:bg-slate-50"
                    onClick={() => {
                      onChange(qualityCase.id);
                      setSearch("");
                    }}
                  >
                    {qualityCaseLabel(qualityCase.id)} · {qualityCase.title}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          {caseId != null ? (
            <button
              type="button"
              className="mt-1 text-xs text-slate-600 hover:underline"
              onClick={() => onChange(null)}
            >
              Remove case link
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
}

export function CorrectiveActionDetailPage() {
  const { id: idParam } = useParams<{ id: string }>();
  const reportId = Number(idParam);
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();
  const writesAllowed = useHubModuleWrite("quality");

  const detailQuery = useQuery({
    queryKey: ["quality", "corrective-action", reportId],
    queryFn: () => fetchCorrectiveActionDetail(reportId),
    enabled: Number.isFinite(reportId) && reportId > 0,
  });

  const [form, setForm] = useState<FormState | null>(null);
  const [baseline, setBaseline] = useState<FormState | null>(null);
  const [commentText, setCommentText] = useState("");

  useEffect(() => {
    if (detailQuery.data?.report) {
      const next = formFromReport(detailQuery.data.report);
      setForm(next);
      setBaseline(next);
    }
  }, [detailQuery.data?.report]);

  const dirty = useMemo(() => {
    if (form == null || baseline == null) return false;
    return JSON.stringify(form) !== JSON.stringify(baseline);
  }, [form, baseline]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (form == null || baseline == null) throw new Error("Form is not ready.");
      const patch = buildPatch(form, baseline);
      if (Object.keys(patch).length === 0) return detailQuery.data?.report;
      return updateCorrectiveAction(reportId, patch);
    },
    onSuccess: async (updated) => {
      await queryClient.invalidateQueries({ queryKey: ["quality", "corrective-action", reportId] });
      await queryClient.invalidateQueries({ queryKey: CORRECTIVE_ACTIONS_LIST_KEY });
      if (updated) {
        const next = formFromReport(updated);
        setForm(next);
        setBaseline(next);
      }
      showToast({ tone: "success", message: "Corrective action report saved." });
    },
    onError: (error: Error) => showToast({ tone: "error", message: error.message }),
  });

  const savePdfMutation = useMutation({
    mutationFn: () => fetchCorrectiveActionPdf(reportId),
    onSuccess: (payload) => {
      downloadPdfBase64(payload.pdfBase64, payload.filename);
      showToast({ tone: "success", message: "Downloaded CAR PDF." });
    },
    onError: (error: Error) => showToast({ tone: "error", message: error.message }),
  });

  const statusMutation = useMutation({
    mutationFn: (status: CorrectiveActionStatus) => updateCorrectiveAction(reportId, { status }),
    onSuccess: async (updated) => {
      setForm((current) => (current ? { ...current, status: updated.status } : current));
      setBaseline((current) => (current ? { ...current, status: updated.status } : current));
      await queryClient.invalidateQueries({ queryKey: ["quality", "corrective-action", reportId] });
      await queryClient.invalidateQueries({ queryKey: CORRECTIVE_ACTIONS_LIST_KEY });
      showToast({ tone: "success", message: `Status is ${correctiveActionStatusLabel(updated.status)}.` });
    },
    onError: (error: Error) => showToast({ tone: "error", message: error.message }),
  });

  const commentMutation = useMutation({
    mutationFn: () => addCorrectiveActionComment(reportId, commentText.trim()),
    onSuccess: async () => {
      setCommentText("");
      await queryClient.invalidateQueries({ queryKey: ["quality", "corrective-action", reportId] });
      showToast({ tone: "success", message: "Comment added." });
    },
    onError: (error: Error) => showToast({ tone: "error", message: error.message }),
  });

  if (!Number.isFinite(reportId) || reportId < 1) {
    return <p className="text-sm text-red-700">Invalid corrective action report id.</p>;
  }

  if (detailQuery.isLoading || form == null) {
    return <p className="text-sm text-slate-600">Loading corrective action report…</p>;
  }

  if (detailQuery.isError || detailQuery.data == null) {
    return <p className="text-sm text-red-700">{(detailQuery.error as Error)?.message ?? "Report not found."}</p>;
  }

  const report = detailQuery.data.report;
  const activities = detailQuery.data.activities;

  const factsLocked = !writesAllowed || statusReached(form.status, "verification");
  const causeLocked = factsLocked || !statusReached(form.status, "analysis");
  const actionLocked = factsLocked || !statusReached(form.status, "action");
  const verificationLocked = !writesAllowed || form.status !== "verification";
  const identityLocked = !writesAllowed || form.status === "closed" || form.status === "cancelled";

  const selectClassName = cn(
    "mt-1.5 flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm",
    "ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400",
  );

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <PageTitleWithHelp hoverHint={DETAIL_HELP} breadcrumbLabel={correctiveActionLabel(report.id)}>
            {correctiveActionLabel(report.id)}
          </PageTitleWithHelp>
          <div className="flex flex-wrap items-center gap-1" role="group" aria-label="Status">
            {CORRECTIVE_ACTION_FLOW_STATUSES.map((status, index) => {
              const active = form.status === status;
              return (
                <span key={status} className="inline-flex items-center gap-1">
                  {index > 0 ? <ChevronRight className="h-3.5 w-3.5 text-slate-400" aria-hidden /> : null}
                  <span
                    className={
                      active
                        ? "rounded-full bg-brand-600 px-2.5 py-0.5 text-xs font-medium text-white"
                        : "rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-500"
                    }
                    aria-current={active ? "step" : undefined}
                  >
                    {correctiveActionStatusLabel(status)}
                  </span>
                </span>
              );
            })}
            <span className="px-1 text-xs text-slate-400" aria-hidden>
              |
            </span>
            <span
              className={
                form.status === "cancelled"
                  ? "rounded-full bg-brand-600 px-2.5 py-0.5 text-xs font-medium text-white"
                  : "rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-500"
              }
              aria-current={form.status === "cancelled" ? "step" : undefined}
            >
              Cancelled
            </span>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {writesAllowed && nextCorrectiveActionStatus(form.status) != null ? (
            <Button
              disabled={statusMutation.isPending}
              onClick={() => {
                const next = nextCorrectiveActionStatus(form.status);
                if (next != null) statusMutation.mutate(next);
              }}
            >
              {statusMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {nextStageButtonLabel(nextCorrectiveActionStatus(form.status) ?? "closed")}
            </Button>
          ) : null}
          {writesAllowed && form.status !== "cancelled" && form.status !== "closed" ? (
            <Button
              variant="secondary"
              disabled={statusMutation.isPending}
              onClick={() => statusMutation.mutate("cancelled")}
            >
              Cancel
            </Button>
          ) : null}
          <Button
            variant="secondary"
            disabled={dirty || saveMutation.isPending || savePdfMutation.isPending}
            title={dirty || saveMutation.isPending ? "Save the report before you download." : undefined}
            onClick={() => savePdfMutation.mutate()}
          >
            {savePdfMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
            Download
          </Button>
          {writesAllowed ? (
            <HubSaveButton dirty={dirty} isLoading={saveMutation.isPending} onClick={() => saveMutation.mutate()}>
              {saveMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              Save
            </HubSaveButton>
          ) : null}
        </div>
      </header>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(18rem,1fr)]">
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Identification</CardTitle>
            </CardHeader>
            <CardBody className="grid gap-4 md:grid-cols-2">
              <div className="md:col-span-2">
                <Label htmlFor="detail-title">Title</Label>
                <Input
                  id="detail-title"
                  value={form.title}
                  disabled={identityLocked}
                  onChange={(event) => setForm((current) => (current ? { ...current, title: event.target.value } : current))}
                />
              </div>
              <div>
                <Label htmlFor="detail-target-close">Target close date</Label>
                <Input
                  id="detail-target-close"
                  type="date"
                  value={form.targetCloseDate}
                  disabled={identityLocked}
                  onChange={(event) =>
                    setForm((current) => (current ? { ...current, targetCloseDate: event.target.value } : current))
                  }
                />
              </div>
              <div>
                <Label htmlFor="detail-source">Source</Label>
                <select
                  id="detail-source"
                  className={selectClassName}
                  value={form.sourceType}
                  disabled={identityLocked}
                  onChange={(event) =>
                    setForm((current) =>
                      current ? { ...current, sourceType: event.target.value as CorrectiveActionSource } : current,
                    )
                  }
                >
                  {CORRECTIVE_ACTION_SOURCES.map((source) => (
                    <option key={source} value={source}>
                      {correctiveActionSourceLabel(source)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="detail-owner">Owner</Label>
                <QualityOwnerSelect
                  id="detail-owner"
                  value={form.ownerUserId}
                  currentLabel={report.ownerDisplay}
                  disabled={identityLocked}
                  onChange={(ownerUserId) =>
                    setForm((current) => (current ? { ...current, ownerUserId } : current))
                  }
                />
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Problem description</CardTitle>
            </CardHeader>
            <CardBody>
              <TextArea
                value={form.problemDescription}
                disabled={factsLocked}
                rows={5}
                onChange={(event) =>
                  setForm((current) => (current ? { ...current, problemDescription: event.target.value } : current))
                }
                placeholder="Describe the nonconformity, where it was found, and the impact"
              />
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <CardTitleWithHelp hoverHint={CONTAINMENT_HELP}>Immediate containment</CardTitleWithHelp>
            </CardHeader>
            <CardBody>
              <TextArea
                value={form.immediateContainment}
                disabled={factsLocked}
                rows={4}
                onChange={(event) =>
                  setForm((current) => (current ? { ...current, immediateContainment: event.target.value } : current))
                }
                placeholder="Actions taken to control or correct the nonconformity and deal with consequences"
              />
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Root cause analysis</CardTitle>
            </CardHeader>
            <CardBody>
              <TextArea
                value={form.rootCauseAnalysis}
                disabled={causeLocked}
                rows={5}
                onChange={(event) =>
                  setForm((current) => (current ? { ...current, rootCauseAnalysis: event.target.value } : current))
                }
                placeholder="Why did this happen? Use 5-why, fishbone, or other method"
              />
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Corrective action</CardTitle>
            </CardHeader>
            <CardBody className="space-y-4">
              <div>
                <Label htmlFor="detail-corrective-plan">Corrective action plan</Label>
                <TextArea
                  id="detail-corrective-plan"
                  value={form.correctiveActionPlan}
                  disabled={actionLocked}
                  rows={4}
                  onChange={(event) =>
                    setForm((current) => (current ? { ...current, correctiveActionPlan: event.target.value } : current))
                  }
                  placeholder="Planned actions to eliminate the root cause"
                />
              </div>
              <div>
                <Label htmlFor="detail-implementation">Implementation notes</Label>
                <TextArea
                  id="detail-implementation"
                  value={form.implementationNotes}
                  disabled={actionLocked}
                  rows={4}
                  onChange={(event) =>
                    setForm((current) => (current ? { ...current, implementationNotes: event.target.value } : current))
                  }
                  placeholder="What was done, when, and by whom"
                />
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Effectiveness verification</CardTitle>
            </CardHeader>
            <CardBody className="space-y-4">
              <div>
                <Label htmlFor="detail-effectiveness">Result of the review</Label>
                <TextArea
                  id="detail-effectiveness"
                  value={form.effectivenessVerification}
                  disabled={verificationLocked}
                  rows={4}
                  onChange={(event) =>
                    setForm((current) =>
                      current ? { ...current, effectivenessVerification: event.target.value } : current,
                    )
                  }
                  placeholder="Leave blank until people have reviewed whether the action worked"
                />
              </div>
              <div>
                <div className="flex items-center gap-1">
                  <Label htmlFor="detail-continued-review">Scheduled date for continued review</Label>
                  <HoverHintWithGlyph
                    hoverHint="Leave empty for none. The QA meeting picks this report up in the month of this date."
                    size="sm"
                  />
                </div>
                <Input
                  id="detail-continued-review"
                  type="date"
                  value={form.continuedReviewOn}
                  disabled={verificationLocked}
                  onChange={(event) =>
                    setForm((current) => (current ? { ...current, continuedReviewOn: event.target.value } : current))
                  }
                />
              </div>
            </CardBody>
          </Card>

        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Related records</CardTitle>
            </CardHeader>
            <CardBody className="space-y-3">
              <div>
                <p className="text-sm font-medium text-slate-700">Quality event</p>
                {report.qualityEventId != null ? (
                  <p className="mt-1.5 text-sm">
                    <Link className="text-sky-700 hover:underline" to={`/quality/events/${report.qualityEventId}`}>
                      Event {report.qualityEventId}
                    </Link>
                  </p>
                ) : (
                  <p className="mt-1.5 text-sm text-slate-600">No quality event linked.</p>
                )}
              </div>
              <QualityCaseLinkField
                caseId={form.qualityCaseId}
                caseTitle={report.qualityCaseTitle}
                disabled={identityLocked}
                onChange={(qualityCaseId) =>
                  setForm((current) => (current ? { ...current, qualityCaseId } : current))
                }
              />
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Activity log</CardTitle>
            </CardHeader>
            <CardBody className="space-y-4">
              {writesAllowed ? (
                <div className="space-y-2">
                  <Label htmlFor="detail-comment">Add comment</Label>
                  <TextArea
                    id="detail-comment"
                    value={commentText}
                    rows={3}
                    onChange={(event) => setCommentText(event.target.value)}
                    placeholder="Review notes or meeting minutes"
                  />
                  <Button
                    size="sm"
                    disabled={commentMutation.isPending || commentText.trim() === ""}
                    onClick={() => commentMutation.mutate()}
                  >
                    {commentMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    Add comment
                  </Button>
                </div>
              ) : null}
              <ul className="space-y-3">
                {activities.length === 0 ? (
                  <li className="text-sm text-slate-600">No activity yet.</li>
                ) : (
                  activities.map((activity) => (
                    <li key={activity.id} className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm">
                      <p className="font-medium text-slate-900">{activitySummary(activity)}</p>
                      <p className="mt-1 text-xs text-slate-600">
                        {activity.actorDisplay ?? activity.actorEmail} · {formatDateTime(activity.activityAt)}
                      </p>
                      {activity.note && activity.actionType === "comment" ? (
                        <p className="mt-2 whitespace-pre-wrap text-slate-700">{activity.note}</p>
                      ) : null}
                      {correctiveActionActivityChangeText(activity) ? (
                        <p className="mt-2 whitespace-pre-wrap text-slate-700">
                          {correctiveActionActivityChangeText(activity)}
                        </p>
                      ) : null}
                    </li>
                  ))
                )}
              </ul>
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  );
}
